import { Component, OnInit } from "@angular/core";
import { UntypedFormControl } from "@angular/forms";
import { MatLegacyMenuTrigger as MatMenuTrigger } from "@angular/material/legacy-menu";
import { Sort } from "@angular/material/sort";
import { Params } from "@angular/router";
import { currencyDigits } from "@app/_helpers/currency";
import format from "@app/_helpers/date-fns/format";
import { debounceTimeAfterFirst } from "@app/_helpers/debounceAfterTime";
import { traverseAdvanced } from "@app/_helpers/object";
import { createEncryptedRxValue, RXParser } from "@app/_helpers/rx-value";
import { createDateRangeArray, DateRangeType, filterNaN } from "@app/_helpers/utils";
import { DefaultEntityTranslatePipe } from "@app/_pipes/default-entity-translate.pipe";
import { AppService, ThemeType } from "@app/app.service";
import { APP_ASSIGN_COLOR_SCHEME } from "@app/shared/color-schemes/office-scheme";
import { buildRangeQueryParams } from "@app/shared/time-range-picker/range-query-params";
import { TIME_RANGE_DEFAULT_RANGES, TRange } from "@app/shared/time-range-picker/time-range-constants";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { TranslateService } from "@ngx-translate/core";
import { MyTimesQuery, ProjectsQuery, Time, TimesService, UserSettings, UserSettingsQuery } from "@timeghost/web-api";
import { ApexOptions } from "apexcharts";
import { isSameDay, isSameISOWeek, isSameMonth } from "date-fns/esm";
import { merge } from "lodash-es";
import { BehaviorSubject, combineLatest } from "rxjs";
import { distinctUntilChanged, finalize, map, startWith, switchMap } from "rxjs/operators";
import { firstBy } from "thenby";
@UntilDestroy()
@Component({
	selector: "tg-insights-page",
	templateUrl: "./insights-page.component.html",
	styleUrls: ["./insights-page.component.scss"],
	providers: [DefaultEntityTranslatePipe],
})
export class InsightsPageComponent implements OnInit {
	private _activeSort = createEncryptedRxValue<Sort>(`insights.sort`, {
		active: "earnings.balance",
		direction: "desc",
	});
	readonly activeSort$ = this._activeSort.asObservable().pipe(distinctUntilChanged());
	get activeSort() {
		return (
			this._activeSort.value ?? {
				active: "earnings.balance",
				direction: "desc",
			}
		);
	}
	set activeSort(val: Sort) {
		this._activeSort.next(val);
	}
	insightTypeControl = new UntypedFormControl(1);
	insightType$ = this.insightTypeControl.valueChanges.pipe(startWith(this.insightTypeControl.value));
	private _range = createEncryptedRxValue<TRange>(`insights.range`, TIME_RANGE_DEFAULT_RANGES.week, {
		parser: RXParser.DateParser("from", "to"),
	});
	readonly range$ = this._range.asObservable().pipe(distinctUntilChanged());
	get range() {
		return this._range.value;
	}
	set range(val: TRange) {
		this._range.next(val);
	}
	/** Dashboard deep-link query: shared range keys + optional user/project. */
	buildDashboardQueryParams(extra: { user?: string; project?: string } = {}): Params {
		return {
			...buildRangeQueryParams(this.range),
			...extra,
		};
	}
	private _projectsFilter = createEncryptedRxValue<string[]>(() => `insights.projectsFilter`, null);
	readonly projectsFilter$ = this._projectsFilter.asObservable().pipe(distinctUntilChanged());
	get projectsFilter() {
		return this._projectsFilter.value;
	}
	set projectsFilter(val: string[]) {
		this._projectsFilter.next(val);
	}
	private _employeeFilter = createEncryptedRxValue<string[]>(() => `insights.employeeFilter`, null);
	readonly employeeFilter$ = this._employeeFilter.asObservable().pipe(distinctUntilChanged());
	get employeeFilter() {
		return this._employeeFilter.value;
	}
	set employeeFilter(val: string[]) {
		this._employeeFilter.next(val);
	}
	private _times = new BehaviorSubject<Time[]>([]);
	readonly times$ = this._times.asObservable().pipe(distinctUntilChanged(), untilDestroyed(this));
	get times() {
		return this._times.getValue();
	}
	set times(val: Time[]) {
		this._times.next(val);
	}

	contextMenuPosition = { x: "0px", y: "0px" };
	constructor(
		private projectsQuery: ProjectsQuery,
		private myTimesQuery: MyTimesQuery,
		private timesService: TimesService,
		private userSettingsQuery: UserSettingsQuery,
		private appService: AppService,
		private defaultEntityTranslate: DefaultEntityTranslatePipe,
		private translate: TranslateService
	) {}
	openContextMenu(event: MouseEvent, trigger: MatMenuTrigger, data: any) {
		event.stopPropagation(), event.preventDefault();
		this.contextMenuPosition.x = event.clientX + "px";
		this.contextMenuPosition.y = event.clientY + "px";
		trigger.menuData = data;
		trigger.menu.focusFirstItem("mouse");
		trigger.openMenu();
	}
	ngOnInit(): void {
		combineLatest([this.range$])
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(([l], [r]) => {
					const compareProperties = ["from", "to", "rangeType"];
					return JSON.stringify(l, compareProperties) === JSON.stringify(r, compareProperties);
				}),
				switchMap(([range]) => {
					this.isLoading = true;
					return this.timesService
						.getTeamTimesBetweenRange(range.from, range.to)
						.pipe(finalize(() => (this.isLoading = false)));
				})
			)
			.subscribe((x) => (this.times = x ?? []));
	}

	data$ = combineLatest([
		this.times$,
		this.userSettingsQuery.select().pipe(startWith(this.userSettingsQuery.getValue())),
		this.insightType$,
		this.range$,
		this.projectsFilter$.pipe(
			map((entity) => entity || []),
			distinctUntilChanged()
		),
		this.employeeFilter$.pipe(
			map((entity) => entity || []),
			distinctUntilChanged()
		),
	]).pipe(debounceTimeAfterFirst(100));
	data$graph = combineLatest([
		this.data$,
		this.projectsQuery.selectAll(),
		this.appService.selectedTheme$.asObservable(),
	]).pipe(
		untilDestroyed(this),
		map(([[times, user, type, range, projectFilter, userFilter], projects, theme]) => {
			const [seriesRange, seriesType] = createDateRangeArray<number>(range.from, range.to, true);
			const _filteredTimes =
				type === 1
					? projectFilter?.length > 0
						? times.filter((x) => projectFilter.includes(x.project.id))
						: times
					: type === 2
						? userFilter?.length > 0
							? times.filter((x) => userFilter.includes(x.user.id))
							: times
						: times;
			const seriesTypeDateCheck = (dateLeft: string | number | Date, dateRight: string | number | Date) =>
				!!(
					seriesType === "day"
						? isSameDay
						: seriesType === "week"
							? isSameISOWeek
							: seriesType === "month"
								? isSameMonth
								: null
				)?.(new Date(dateLeft), new Date(dateRight));
			let graph: ApexOptions;
			// Project
			if (type === 1) {
				graph = <ApexOptions>{
					series: [
						{
							data: seriesRange.reduce((seriesData: number[], [date]): number[] => {
								const timeSeries = _filteredTimes.filter(
									(x) => seriesTypeDateCheck(date, x.start) && ~~x.amount > 0.0
								);
								seriesData.push(timeSeries.reduce((rateAmount, r) => (rateAmount += r.amount), 0));
								return seriesData;
							}, []),
							name: this.translate.instant("insights.earnings"),
							color: "#51BF43",
						},
						{
							data: seriesRange.reduce((seriesData: number[], [date]): number[] => {
								const timeSeries = _filteredTimes.filter(
									(x) => seriesTypeDateCheck(date, x.start) && ~~x.cost > 0.0
								);
								seriesData.push(timeSeries.reduce((laborAmount, r) => (laborAmount += r.cost), 0));
								return seriesData;
							}, []),
							name: this.translate.instant("insights.loss"),
							color: "#ff0030",
						},
					],
					xaxis: {
						categories: seriesRange.map(([key]) => key),
					},
				};
			} else if (type === 2) {
				// employee
				graph = <ApexOptions>{
					series: [
						{
							data: seriesRange.reduce((seriesData: number[], [date]): number[] => {
								const timeSeries = _filteredTimes.filter(
									(x) => seriesTypeDateCheck(date, x.start) && ~~x.amount > 0.0
								);
								seriesData.push(timeSeries.reduce((rateAmount, r) => (rateAmount += r.amount), 0));
								return seriesData;
							}, []),
							name: this.translate.instant("insights.earnings"),
							color: "#51BF43",
						},
						{
							data: seriesRange.reduce((seriesData: number[], [date]): number[] => {
								const timeSeries = _filteredTimes.filter(
									(x) => seriesTypeDateCheck(date, x.start) && ~~x.cost > 0.0
								);
								seriesData.push(timeSeries.reduce((laborAmount, r) => (laborAmount += r.cost), 0));
								return seriesData;
							}, []),
							name: this.translate.instant("insights.loss"),
							color: "#ff0030",
						},
					],
					xaxis: {
						categories: seriesRange.map(([key]) => key),
					},
				};
			}
			return [graph, user, seriesType, theme];
		}),
		distinctUntilChanged(),
		map(([x, user, seriesType, theme]: [ApexOptions, UserSettings, DateRangeType, ThemeType]) => {
			const formatYAxis = (v: number): any => {
				if (v === 0.0) return undefined;
				return `${user.workspace.settings?.currency || "$"} ${v.toLocaleString(user.settings?.languageSetting, {
					maximumFractionDigits: 2,
				})}`;
			};
			return (
				!x ||
				merge(x, <Partial<ApexOptions>>{
					colors: [...APP_ASSIGN_COLOR_SCHEME],
					dataLabels: {
						enabled: false,
					},
					theme: {
						mode: theme === "dark" ? "dark" : "light",
					},
					yaxis: {
						opposite: true,
						labels: {
							formatter: formatYAxis,
							offsetX: -8,
						},
					},
					markers: {
						size: 0,
						showNullDataPoints: false,
						strokeWidth: 0,
						colors: [theme === "dark" ? "#fff" : "#303030"],
						hover: {
							size: 4,
						},
					},
					grid: {
						borderColor: theme === "dark" ? "#303030" : "#efefef",
						clipMarkers: false,
						padding: {
							right: 40,
						},
					},
					xaxis: {
						tooltip: {
							enabled: false,
						},
						labels: {
							formatter: (v: any) => {
								return v
									? format(
											v,
											seriesType === "day"
												? `MMM'\n'do`
												: seriesType === "week"
													? `MMM'\nWeek 'I`
													: seriesType === "month"
														? `MMM`
														: null,
											{
												weekStartsOn: 1,
												locale: user.settings.languageSetting,
											}
										).split("\n")
									: "-";
							},
						},
					},
				})
			);
		})
	);
	isLoading: boolean = false;
	data$table$employee = combineLatest([this.data$, this.projectsQuery.selectAll(), this.activeSort$]).pipe(
		untilDestroyed(this),
		map(([[_times, user, type, range, projectFilter, userFilter], projects, sort]) => {
			const times = userFilter?.length > 0 ? _times.filter((x) => userFilter.includes(x.user.id)) : _times;
			let entries: any[] = Object.values(
				times.reduce((l, r) => {
					if (filterNaN(r.amount) || filterNaN(r.cost)) return l;
					const userId = r.user.id;
					const item = l[userId];
					if (item) {
						l[userId] = {
							...item,
							// @ts-ignore
							amount: item.amount + r.amount,
							cost: item.cost + r.cost,
							billedTimeDiff: item.billedTimeDiff + (r.billable ? r.timeDiff : 0),
							timeDiff: item.timeDiff + r.timeDiff,
						};
					} else {
						l[userId] = {
							// @ts-ignore
							amount: r.amount,
							cost: r.cost,
							user: r.user,
							project: r.project,
							client: r.client,
							refs: {
								outlook: r.outlookCalenderReference,
							},
							billable: r.billable,
							timeDiff: r.timeDiff,
							billedTimeDiff: r.billable ? r.timeDiff : 0,
							name: r.user?.name,
						};
					}
					return l;
				}, {})
			).map(parseEarnings);
			if (sort?.direction) {
				entries = entries.sort((a, b) => {
					const sortData = {
						current: traverseAdvanced<any>(sort.direction === "asc" ? a : b, sort.active),
						next: traverseAdvanced<any>(sort.direction === "asc" ? b : a, sort.active),
					};
					switch (typeof sortData.current) {
						case "string":
							return (sortData.current as string).localeCompare(sortData.next as string);
						case "number":
							return sortData.current - sortData.next;
						case "boolean":
							return sortData.current.asNumber() - sortData.next.asNumber();
					}
				});
			}
			const earnings: number = entries
					.filter((r) => r.earnings?.rateCost > 0.0)
					.reduce((l: number, r: any) => {
						return (l += r.earnings.rateCost);
					}, 0),
				labor: number = entries
					.filter((r) => r.earnings?.laborCost > 0.0)
					.reduce((l: number, r: any) => {
						return (l += r.earnings?.laborCost);
					}, 0);

			const currency = user.workspace.settings?.currency || "$",
				currencyFormat = currencyDigits(currency);
			return {
				entries,
				earnings,
				labor,
				balance: earnings - labor,
				currency,
				currencyFormat,
				type: this.insightTypeControl.value,
			};
		})
	);
	data$table$project = combineLatest([this.data$, this.projectsQuery.selectAll(), this.activeSort$]).pipe(
		untilDestroyed(this),
		map(([[_times, user, type, range, projectFilter, userFilter], projects, sort]) => {
			const times =
				projectFilter?.length > 0 ? _times.filter((x) => projectFilter.includes(x.project.id)) : _times;
			let entries: any[] = Object.values(
				times.reduce((l, r) => {
					if (filterNaN(r.amount) || filterNaN(r.cost)) return l;
					const keyId = r.project.id;
					const item = l[keyId];
					if (item) {
						l[keyId] = {
							...item,
							amount: item.amount + r.amount,
							cost: item.cost + r.cost,
							billedTimeDiff: item.billedTimeDiff + (r.billable ? r.timeDiff : 0),
							timeDiff: item.timeDiff + r.timeDiff,
						};
					} else {
						l[keyId] = {
							amount: r.amount,
							cost: r.cost,
							user: r.user,
							project: r.project,
							client: r.client,
							refs: {
								outlook: r.outlookCalenderReference,
							},
							billable: r.billable,
							timeDiff: r.timeDiff,
							billedTimeDiff: r.billable ? r.timeDiff : 0,
							name: r.project?.name,
						};
					}
					return l;
				}, {})
			).map(parseEarnings);

			if (sort?.direction) {
				entries = entries.sort((a, b) => {
					const sortData = {
						current: traverseAdvanced<any>(sort.direction === "asc" ? a : b, sort.active),
						next: traverseAdvanced<any>(sort.direction === "asc" ? b : a, sort.active),
					};
					switch (typeof sortData.current) {
						case "string":
							return (sortData.current as string).localeCompare(sortData.next as string);
						case "number":
							return sortData.current - sortData.next;
						case "boolean":
							return sortData.current.asNumber() - sortData.next.asNumber();
					}
				});
			}
			const earnings: number = entries
					.filter((r) => r.earnings?.rateCost > 0.0)
					.reduce((l: number, r: any) => {
						return (l += r.earnings.rateCost);
					}, 0),
				labor: number = entries
					.filter((r) => r.earnings?.laborCost > 0.0)
					.reduce((l: number, r: any) => {
						return (l += r.earnings.laborCost);
					}, 0);
			const currency = user.workspace.settings?.currency || "$",
				currencyFormat = currencyDigits(currency);
			return {
				entries,
				earnings,
				labor,
				balance: earnings - labor,
				currency,
				currencyFormat,
				type: this.insightTypeControl.value,
			};
		})
	);

	changeDataSort(ev: Sort) {
		this.activeSort = ev.direction ? ev : null;
	}
	trackId(index: number, { id }: { id: string }) {
		return id;
	}
}
interface User {
	id: string;
	name: string;
}
const parseEarnings = (x: any) => {
	const laborCost = x.cost,
		rateCost = x.amount,
		gross = laborCost + rateCost,
		profitDiff = rateCost > 0.0 ? ((100 / gross) * rateCost).clamp(0.0, 100.0) : 0,
		lossDiff = laborCost > 0.0 ? ((100 / gross) * laborCost).clamp(0.0, 100.0) : 0;
	x.earnings = {
		loss: gross - rateCost,
		profit: gross - laborCost,
		lossDiff,
		profitDiff,
		laborCost,
		rateCost,
		gross,
		balance: rateCost - laborCost,
		showGraph: profitDiff !== 0.0 || lossDiff !== 0.0,
	};
	return x;
};
const sortEarnings = firstBy<{ amount: number }>((x) => ~~x.amount, "desc").thenBy<{ cost: number }>(
	(x) => x.cost,
	"desc"
);
